/**
 * RD-SEC-015 — save-destination handlers for the recovery-codes step
 * ("B · Save first"): Share (touch, when supported), Download (web / no
 * `navigator.share`), and Copy (everywhere). `MfaStepRecoveryCodes` is the
 * one implementation — both the enrollment stepper's "codes" step and
 * `MfaRegenerateCodesModal`'s codes screen render it, so they share this
 * module instead of drifting two copies.
 */
import { copyToClipboard } from '../ui/copyToClipboard';
import { recoveryCodesText, downloadRecoveryCodes } from './recoveryCodeExport';

/** True when the Web Share API is available — drives the compact "Guardar en
 *  otra app" row; falls back to the download row when false (AC 15). */
export function canShareRecoveryCodes() {
  return typeof navigator !== 'undefined' && typeof navigator.share === 'function';
}

/**
 * Share the codes via the OS share sheet. Returns `{ ok, aborted }` —
 * `aborted` is true only for the user's own cancel (`AbortError`), which must
 * NOT flip the row to "done" (AC 15).
 */
export async function shareRecoveryCodes(codes, { title, text } = {}) {
  if (!canShareRecoveryCodes()) return { ok: false, aborted: false, unsupported: true };
  const body = text ? `${text}\n\n${recoveryCodesText(codes)}` : recoveryCodesText(codes);
  try {
    await navigator.share({ title, text: body });
    return { ok: true, aborted: false, unsupported: false };
  } catch (err) {
    const aborted = Boolean(err) && err.name === 'AbortError';
    return { ok: false, aborted, unsupported: false };
  }
}

/** Copy the plain newline-joined codes to the clipboard. */
export async function copyRecoveryCodesToClipboard(codes) {
  return copyToClipboard(recoveryCodesText(codes));
}

/** Download the codes as a `.txt` — thin re-export so callers only need this module. */
export function triggerRecoveryCodesDownload(codes, headerText, owner = {}) {
  downloadRecoveryCodes(codes, headerText, owner);
}
